import ModalConfirm from '@components/AppModal/ModalConfirm'
import WarningExitWithoutSave from '@components/AppModal/WarningExitWithoutSave'
import EmailTemplatePreview from '@components/EmailTemplatePreview'
import PageModal from '@components/PageModal'
import RingCentral from '@components/RingCentral'
import StoreContextWrapper from '@components/StoreContextWrapper'
import ThemeSettings from '@components/ThemeSettings'
import { queryClient } from '@services/baseService'
import appStore from '@stores/appStore'
import { initAppConsole } from '@utils/appConsole'
import { ConfigProvider } from 'antd'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import isoWeek from 'dayjs/plugin/isoWeek'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import localeData from 'dayjs/plugin/localeData'
import minMax from 'dayjs/plugin/minMax'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'
import { createBrowserHistory } from 'history'
import { configure } from 'mobx'
import { observer } from 'mobx-react'
import { syncHistoryWithStore } from 'mobx-react-router'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from 'react-query'
import { Router } from 'react-router'
import TelemetryProvider from 'tracking/applicationInsight/telemetry-provider'
import { getAppInsights } from 'tracking/applicationInsight/telemetryService'
import App from './App'
import router from './stores/router'
import * as bugTracking from './tracking/bugTracking'
import { initTranslator } from './translator'

// const QuickSetting = loadable(() => import('@components/QuickSetting'))

dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(duration)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(minMax)
dayjs.extend(isBetween)
dayjs.extend(isoWeek)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)
dayjs.extend(relativeTime)
dayjs.extend(customParseFormat)

initAppConsole()

configure({ enforceActions: 'never' })

window.APP_CONFIG = {
  maxResultCount: 20,
}
const browserHistory = createBrowserHistory()
const history = syncHistoryWithStore(browserHistory, router)
let appInsights = null

const RootComponent = observer(() => {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1fc0da',
          fontFamily: 'Nunito, Helvetica, sans-serif',
        },
      }}
    >
      <Router history={history}>
        <TelemetryProvider
          instrumentationKey={process.env.VITE_APPINSIGHT_INSTRUMENTATION_KEY}
          after={() => {
            appInsights = getAppInsights()
            appStore.appInsights = appInsights
          }}
          history={history}
        >
          <QueryClientProvider client={queryClient}>
            <StoreContextWrapper storeKey="">
              <App />
              {appStore.isOpenConfirmModal && (
                <ModalConfirm
                  modalParams={appStore.confirmModalParams}
                  onCancel={() => appStore.closeConfirmModal()}
                />
              )}
              {appStore.showThemeSetting && <ThemeSettings />}
              {appStore.isOpenWarningWithoutSave && (
                <WarningExitWithoutSave
                  modalParams={appStore.warningWithoutSaveParams}
                  onCancel={() => appStore.closeWarningExitWithoutSave()}
                />
              )}

              <PageModal />
              <RingCentral />
              {/* {import.meta.env.DEV && <QuickSetting />} */}

              {appStore.previewEmailTemplateData.isOpen && (
                <EmailTemplatePreview />
              )}
            </StoreContextWrapper>
          </QueryClientProvider>
        </TelemetryProvider>
      </Router>
    </ConfigProvider>
  )
})

if (import.meta.env.PROD) {
  bugTracking.init()
}

initTranslator({
  language: 'MLP',
})

const container = document.getElementById('root')
const root = createRoot(container)
root.render(<RootComponent />)
